import { useState, useEffect } from 'react';
import { Grid, Typography, Box } from '@mui/material';
import FormCard from '../components/FormCard';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../context/MemberProfileContext';
import { fetchEventTypes } from '../api/memberServices';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
export default function EventTypesForm() {
  const { accessToken, signOut } = useAuth();
  const { memberProfile, setMemberProfile, setPersonName, eventTypes, setEventTypes } = useMember();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [dataFields, setDataFields] = useState([]);

  // Effects
  useEffect(() => {
    if (accessToken) {
      fetchEvents();
      memberProfileFetch();
    }
  }, [accessToken]);

  // Handlers
  const handleLogout = () => {
    signOut();
    navigate('/');
  };

  // API calls
  const fetchEvents = async () => {
    try {
      const { response, data } = await fetchEventTypes(accessToken);

      if (response.ok) {
        setDataFields(data);
        setEventTypes(data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fetch events failed');
      }
    } catch (error) {
      console.error("Error fetching event types:", error);
      signOut();
      navigate('/');
    }
  };

  const memberProfileFetch = async () => {
    try {
      const response = await fetch(`${API_URL}/gquery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-cmdfw-jwt-token': `${accessToken}`,
        },
        body: JSON.stringify({
          query: `
            query {
              memProfile {
                primary {
                  memberId
                  memberType
                  firstName
                  lastName
                  mobileNumber
                  email
                },
                spouse {
                  memberId
                  memberType
                  firstName
                  lastName
                  mobileNumber
                  email
                  status
                },
                children {
                  memberId
                  memberType
                  firstName
                  lastName
                  fullName
                  gender
                  mobileNumber
                  email
                }
                login {
                  showWelcome
                  memberStatus
                }
                houseHold {
                  householdId
                  streetAddress
                  city
                  state
                  postalCode
                }
                menus {
                  menuId
                  text
                  uri
                  desc
                  displayOrder
                  children {
                    menuId
                    text
                    uri
                    desc
                    displayOrder
                  }
                }                           
              }
            }
          `,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        data.data.memProfile.spouse = data.data.memProfile.spouse || {
          firstName: '', 
          lastName: '', 
          mobileNumber: '', 
          email: ''
        };
        setMemberProfile(data.data.memProfile);
        setPersonName(data.data.memProfile.primary.firstName);

        if (data.data.memProfile.login.showWelcome === "true") {
          navigate('/HouseholdMembers');
        }
      } else {
        if (response.status === 403) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'OTP expired');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Member Profile fetch failed');
        }
      }
    } catch (error) {
      console.error("Error fetching Member Profile:", error);
      signOut();
      navigate('/');
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', // Lighter background
        py: 4
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              color: '#2c3e50',
              mb: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            CMDFW Registration System
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#6c757d', // Lighter text color
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Choose from our available events and programs
          </Typography>
        </Box>

        {/* Event Cards Grid */}
        <Grid container spacing={4} justifyContent="center" sx={{ px: 2 }}>
          {dataFields
            .filter((dataField) => dataField.parentEventTypeId === null)
            .map((dataField, index) => (
              <Grid 
                item
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // Responsive grid without fixed breakpoints since cards are fixed size
                  minWidth: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: 'none' }
                }}
              >
                <Box
                  sx={{
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      '& .form-card': {
                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)' // Lighter shadow
                      }
                    }
                  }}
                >
                  <FormCard
                    title={dataField.name}
                    route={dataField.route}
                    buttonName={
                      dataField.name === 'Donation' ? 'Donate' : 
                      dataField.name === 'Banquet' ? 'Purchase' : 
                      'Register'
                    }
                    eventImage={dataField.imagePath}
                    className="form-card"
                  />
                </Box>
              </Grid>
            ))
          }
        </Grid>

        {/* Footer Section */}
        {dataFields.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#adb5bd', // Lighter loading text
                fontStyle: 'italic'
              }}
            >
              Loading events...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
